/**
 * FILE-OVERVIEW:
 * - 役割: Cognitoセッションからログインユーザー情報を構築。
 * - 主な連携: App.js, Calendar/index.js, Memo/index.js
 * - 本番運用上の注意: グループ判定ロジック変更は権限制御の挙動に直結する。
 */

import { useState, useEffect } from 'react';
import { getCurrentUserInfo } from '../services/calendarApi';
import type { CurrentUser } from '../services/calendarApi';

/**
 * 画面が扱うログインユーザー。
 * 取得に失敗した場合は一部の項目しか埋まらないため Partial で受ける
 */
export type AuthUser = Partial<CurrentUser>;

/**
 * 認証済みユーザー情報を画面向けに提供するカスタムフック。
 *
 * 入力:
 * - なし
 *
 * 出力:
 * - currentUser: Cognito由来のユーザー情報（未認証時は isAuthenticated=false）
 * - isLoading: 初回取得中フラグ
 * - displayName: 画面表示用ユーザー名（name > username > email > ゲスト）
 *
 * 副作用:
 * - マウント時に calendarApi.getCurrentUserInfo を1回呼び出す
 *
 * 本番運用上の注意:
 * - currentUser のプロパティ名は多数画面で参照されるため変更時は横断確認が必要。
 */
export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const userInfo = await getCurrentUserInfo();
        setCurrentUser(userInfo);
      } catch (error) {
        console.error('ユーザー情報の取得に失敗:', error);
        setCurrentUser({
          isAuthenticated: false,
          isDesk: false,
          isReporter: false
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserInfo();
  }, []);

  const displayName = currentUser?.name || currentUser?.username || currentUser?.email || 'ゲスト';

  return { currentUser, isLoading, displayName };
};